import { useState, useEffect, useCallback } from 'react';
import { useHayCambios } from '../hooks/useHayCambios';
import { useCierreConfirmado } from '../hooks/useCierreConfirmado';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useServerPage } from '../hooks/useServerPage';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { useProveedores } from '../hooks/useProveedores';
import { Modal } from '../components/Modal';
import { Pagination } from '../components/Pagination';
import { HelpTip } from '../components/HelpTip';
import { VerInstructivo } from '../components/VerInstructivo';
import { useToast } from '../components/Toast';
import { api, extractErrorMessage } from '../api/client';
import { Button } from '../components/Button';
import { StockSku, type ResumenStockSku } from '../components/StockSku';
import { VariantesSku } from '../components/VariantesSku';
import type { VariantesDeSku } from '../lib/variantes';
import { CodigoArticulo } from '../components/CodigoArticulo';
import { etiquetaArticulo, useIdentidades } from '../lib/identidadArticulos';
import { SincronizarConMl, PanelDesplegable } from './SincronizarConMl';
import { PublicacionesMl } from './PublicacionesMl';
import { HistorialMl } from './HistorialMl';
import { VistaPreviaSyncSku } from './VistaPreviaSyncSku';

interface Articulo {
  skuInterno: string; descripcion?: string; abcCategoria?: string; abcRotacion?: string;
  abcRentabilidad?: string; abcInversion?: string;
  costoPromedio?: string; precioVenta?: string; margenBruto?: string; margenPct?: string;
  ean?: string; marca?: string; titulo?: string; unidadMedidaBase?: string;
  unidadMedidaAlt?: string; factorConversion?: string;
  pesoKg?: string; largoCm?: string; anchoCm?: string; altoCm?: string;
  activo?: boolean; mlaId?: string;
  proveedorEstandarId?: number; proveedorEstandar?: { id: number; nombre: string };
  /** Code users see (falls back to the SKU) and identity supplier: supplier + code identify the article. */
  codigo?: string | null; proveedorIdentidad?: { id: number; nombre: string } | null;
  descuento?: string;
  alicuotaIva?: string;
}
/** Alícuotas de IVA argentinas (%). El IVA es un atributo del SKU. */
const ALICUOTAS_IVA = ['0', '2.5', '5', '10.5', '21', '27'];
const alicuotaIvaTexto = (v?: string | number | null) => (v === undefined || v === null || v === '' ? '21' : String(Number(v)));
interface Unidad { codigo: string; descripcion: string; abreviatura: string; }
interface BomItem { skuPadre: string; skuHijo: string; cantidad: string; hijo?: { skuInterno: string; descripcion?: string; unidadMedidaBase?: string } }
interface DondeSeUsaItem { skuPadre: string; cantidad: string; padre?: { skuInterno: string; descripcion?: string; unidadMedidaBase?: string; activo?: boolean } }
interface Marca { nombre: string; }
interface PublicacionDeSku {
  mlaId: string; variantId: string | null; origen: 'vinculo' | 'catalogo';
  agrupadorVariantes: string | null; varianteTexto: string | null;
  publicacion: { titulo: string | null; status: string | null; precio: number | null; stockDisponibleMl: number | null; userProductId: string | null } | null;
  /** Stock que se publicaría con "stock desde BOM" (tope y stock de seguridad incluidos). */
  stockCalculado?: number | null;
}
interface PublicacionesDeSku {
  skuInterno: string;
  /** Stock del SKU (físico, reservado, disponible; packs/combos en unidades del pack). null si no se pudo leer. */
  stock?: ResumenStockSku | null;
  directas: PublicacionDeSku[];
  indirectas: Array<{ skuInterno: string; titulo: string | null; descripcion: string | null; activo: boolean; camino: string[]; cantidad: number; publicaciones: PublicacionDeSku[] }>;
}

/** Tabla compacta de publicaciones de ML (usada en el detalle del SKU). */
function TablaPublicacionesSku({ items }: { items: PublicacionDeSku[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-slate-50"><tr>
          <th className="text-left px-2 py-1">MLA</th><th className="text-left px-2 py-1">Variante</th>
          <th className="text-left px-2 py-1">Título ML</th><th className="text-left px-2 py-1">Estado</th>
          <th className="text-right px-2 py-1">Precio</th><th className="text-right px-2 py-1">Stock ML</th>
          <th className="text-right px-2 py-1" title="Stock calculado desde la composición (combos desde sus componentes), con tope y unidades que no se publican">Stock calculado</th>
          <th className="text-left px-2 py-1">User product</th>
        </tr></thead>
        <tbody>{items.map(p => (
          <tr key={`${p.mlaId}-${p.variantId ?? ''}-${p.origen}`} className="border-t border-slate-50">
            <td className="px-2 py-1 font-medium">{p.mlaId}{p.origen === 'catalogo' && <span className="text-slate-400 ml-1" title="MLA principal cargado en el catálogo">(catálogo)</span>}</td>
            <td className="px-2 py-1">{[p.varianteTexto, p.variantId].filter(Boolean).join(' · ') || '—'}</td>
            <td className="px-2 py-1">{p.publicacion?.titulo || <span className="text-slate-400">Sin sincronizar</span>}</td>
            <td className="px-2 py-1">{p.publicacion?.status || '—'}</td>
            <td className="text-right px-2 py-1">{p.publicacion?.precio != null ? `$${p.publicacion.precio.toLocaleString('es-AR')}` : '—'}</td>
            <td className="text-right px-2 py-1">{p.publicacion?.stockDisponibleMl ?? '—'}</td>
            <td className={`text-right px-2 py-1 ${p.stockCalculado != null && p.publicacion?.stockDisponibleMl != null && p.stockCalculado !== p.publicacion.stockDisponibleMl ? 'font-semibold text-amber-700' : ''}`}>{p.stockCalculado ?? '—'}</td>
            <td className="px-2 py-1">{p.publicacion?.userProductId || '—'}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

// ---- Badge helper (Base tab: marca/proveedor pills) ----
function ArticuloBadge({ label, color }: { label: string; color: 'blue'|'green'|'orange'|'gray'|'red' }) {
  const cls: Record<string, string> = {
    blue:   'bg-blue-50 text-blue-700 border border-blue-200',
    green:  'bg-emerald-50 text-emerald-700 border border-emerald-200',
    orange: 'bg-brand-50 text-brand-700 border border-brand-200',
    gray:   'bg-slate-100 text-slate-600 border border-slate-200',
    red:    'bg-red-50 text-red-700 border border-red-200',
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls[color]}`}>{label}</span>;
}

// ---- Product row ----
function ArticuloRow({ art, onClick }: { art: Articulo; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      className="flex items-center gap-4 px-5 py-4 border-b border-slate-100 hover:bg-brand-50/40 cursor-pointer transition-colors group"
    >
      {/* Image placeholder */}
      <div className="shrink-0 w-14 h-14 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-300">
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      </div>

      {/* Main info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-xs text-slate-400 font-mono">{art.codigo || art.skuInterno}</span>
          {art.ean && <span className="text-xs text-slate-300">| EAN: {art.ean}</span>}
          {art.mlaId && <span className="text-xs text-slate-300">| MLA: {art.mlaId}</span>}
        </div>
        <p className="text-sm font-semibold text-slate-800 truncate">{art.descripcion || art.titulo || '—'}</p>
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
          {art.marca && <ArticuloBadge label={art.marca} color="gray" />}
          {art.proveedorIdentidad && <ArticuloBadge label={art.proveedorIdentidad.nombre} color="blue" />}
          {art.proveedorEstandar && art.proveedorEstandar.id !== art.proveedorIdentidad?.id && <ArticuloBadge label={art.proveedorEstandar.nombre} color="orange" />}
          {art.activo === false && <ArticuloBadge label="Inactivo" color="red" />}
        </div>
      </div>

      {/* Action */}
      <button
        onClick={e => { e.stopPropagation(); onClick(); }}
        className="shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg border border-brand-500 text-brand-600 hover:bg-brand-500 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
      >
        Ver
      </button>
    </div>
  );
}

const articuloVacio = () => ({
  codigo:'',proveedorId:'',descripcion:'',abcCategoria:'',abcRotacion:'',abcRentabilidad:'',
  abcInversion:'',costoPromedio:'',precioVenta:'',margenBruto:'',margenPct:'',
  ean:'',marca:'',titulo:'',unidadMedidaBase:'UN',unidadMedidaAlt:'',factorConversion:'',
  pesoKg:'',largoCm:'',anchoCm:'',altoCm:'',proveedorEstandarId:'',mlaId:'',descuento:'',alicuotaIva:'21'
});

const SOLAPAS = ['productos', 'mercado-libre'] as const;
type Solapa = (typeof SOLAPAS)[number];
const ROTULOS: Record<Solapa, string> = { productos: 'Productos', 'mercado-libre': 'Mercado Libre' };

/**
 * ?tab=<solapa> abre esa solapa. Las solapas viejas (publicaciones, sincronizar, sync, stock) ahora son partes del bloque
 * Mercado Libre; ?tab=historial o ?ver=historial abren además el Historial. Lo desconocido abre Productos.
 */
export function solapaDeUrl(params: URLSearchParams): { solapa: Solapa; historial: boolean } {
  const tab = params.get('tab');
  const historial = tab === 'historial' || params.get('ver') === 'historial';
  if (historial || ['mercado-libre', 'publicaciones', 'sincronizar', 'sync', 'stock'].includes(tab ?? '')) return { solapa: 'mercado-libre', historial };
  return { solapa: 'productos', historial: false };
}

/** Enlaces viejos: la conexión con Mercado Libre pasó a Configuración → Mercado Libre y catálogo. */
export function destinoEnlaceViejo(params: URLSearchParams): string | null {
  const ml = params.get('ml');
  return ml ? `/configuracion-mercado-libre?ml=${encodeURIComponent(ml)}` : null;
}

export function BaseYPublicacionesPage() {
  const [searchParams] = useSearchParams();
  const destino = destinoEnlaceViejo(searchParams);
  return destino ? <Navigate to={destino} replace /> : <BaseYPublicaciones />;
}

function BaseYPublicaciones() {
  const [searchParams] = useSearchParams();
  const { solapa, historial } = solapaDeUrl(searchParams);
  const [activeTab, setActiveTab] = useState<Solapa>(solapa);
  const toast = useToast();

  // ================= Base (Articulos) state =================
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [filters, setFilters] = useState<Record<string, string>>({});
  const hasFilters = Object.values(filters).some(v => v);
  const { data, loading, total, refetch } = useServerPage<Articulo>(
    '/api/v3/catalogo', page, pageSize, search, 'q',
    hasFilters ? filters : undefined,
    { serverSideFilters: true, sortKey: 'skuInterno', sortDir: 'asc' },
  );
  const { data: unidades } = useApi<Unidad[]>('/api/v3/unidades-medida');
  const { data: marcasList } = useApi<Marca[]>('/api/v3/marcas');
  const { data: proveedoresList } = useProveedores();
  const { can } = useAuth();

  const paged = filters.margenPct
    ? data.filter(row => String(row.margenPct ?? '').toLowerCase().includes(filters.margenPct.toLowerCase()))
    : data;

  const [detailOpen, setDetailOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selected, setSelected] = useState<Articulo | null>(null);
  const [bomItems, setBomItems] = useState<BomItem[]>([]);
  const [dondeSeUsa, setDondeSeUsa] = useState<DondeSeUsaItem[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [umError, setUmError] = useState('');
  const [marcaInput, setMarcaInput] = useState('');
  const [marcaSuggestions, setMarcaSuggestions] = useState<Marca[]>([]);
  const [showMarcaDropdown, setShowMarcaDropdown] = useState(false);
  const [editMarcaInput, setEditMarcaInput] = useState('');
  const [showEditMarcaDropdown, setShowEditMarcaDropdown] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createTab, setCreateTab] = useState('basicos');
  const [editTab, setEditTab] = useState('basicos');
  const [detailTab, setDetailTab] = useState('basicos');
  const tabs = [
    { key: 'basicos', label: 'Básicos' },
    { key: 'dimensiones', label: 'Dimensiones' }, { key: 'unidades', label: 'Unidades' },
  ];
  const tabBar = (active: string, setFn: (k: string) => void) => (
    <div className="flex flex-nowrap overflow-x-auto border-b border-slate-200 mb-3">
      {tabs.map(t => (
        <button key={t.key} onClick={() => setFn(t.key)}
          className={`shrink-0 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${active === t.key ? 'border-brand-600 text-brand-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
          {t.label}
        </button>
      ))}
    </div>
  );
  const [form, setForm] = useState(articuloVacio);
  const cerrarNuevo = () => { setModalOpen(false); setMarcaInput(''); setForm(articuloVacio()); };
  const cierreNuevo = useCierreConfirmado(useHayCambios({ form, marcaInput }, modalOpen), cerrarNuevo);
  // En el detalle solo hay algo que perder en modo edición.
  const cierreDetalle = useCierreConfirmado(useHayCambios(selected, detailOpen && editMode), () => { setDetailOpen(false); setEditMode(false); });
  const [mlaIds, setMlaIds] = useState<{id:number;mlaId:string;activo?:boolean}[]>([]);
  const [publicacionesSku, setPublicacionesSku] = useState<PublicacionesDeSku | null>(null);
  const identidad = useIdentidades((publicacionesSku?.indirectas ?? []).flatMap(i => i.camino));
  const [variantesSku, setVariantesSku] = useState<VariantesDeSku | null>(null);
  const [newMlaId, setNewMlaId] = useState('');

  const loadBom = useCallback(async (sku: string) => {
    if (!can('bom', 'ver')) return;
    try { const r = await api.get<BomItem[]>(`/api/v3/bom?sku=${sku}`); setBomItems(r??[]); }
    catch { /* no perms */ }
  }, [can]);

  const loadDondeSeUsa = useCallback(async (sku: string) => {
    if (!can('bom', 'ver')) return;
    try { const r = await api.get<DondeSeUsaItem[]>(`/api/v3/bom/${sku}/donde-se-usa`); setDondeSeUsa(r??[]); }
    catch { /* no perms */ }
  }, [can]);

  useEffect(() => { if (selected?.skuInterno) { loadBom(selected.skuInterno); loadDondeSeUsa(selected.skuInterno); } }, [selected, loadBom, loadDondeSeUsa]);

  const loadMlaIds = useCallback(async (sku: string) => {
    try { const r = await api.get<{id:number;mlaId:string}[]>(`/api/v3/catalogo/${sku}/mla`); setMlaIds(r??[]); }
    catch { setMlaIds([]); }
  }, []);
  useEffect(() => { if (selected?.skuInterno) loadMlaIds(selected.skuInterno); }, [selected, loadMlaIds]);

  // ¿En qué publicaciones se usa este SKU? (directas + vía BOM). Sólo depende del SKU, no de la edición.
  const selectedSku = selected?.skuInterno;
  useEffect(() => {
    setPublicacionesSku(null); setVariantesSku(null);
    if (!selectedSku) return;
    let vigente = true;
    api.get<PublicacionesDeSku>(`/api/v3/catalogo/${encodeURIComponent(selectedSku)}/publicaciones`)
      .then(r => { if (vigente) setPublicacionesSku(r ?? null); })
      .catch(() => { /* sin permiso o sin datos: la sección no se muestra */ });
    return () => { vigente = false; };
  }, [selectedSku]);

  // Después de empezar/dejar de sincronizar desde la ficha: vuelve a pedir las publicaciones sin vaciar la ficha.
  const recargarFicha = () => {
    if (!selectedSku) return;
    api.get<PublicacionesDeSku>(`/api/v3/catalogo/${encodeURIComponent(selectedSku)}/publicaciones`)
      .then(r => setPublicacionesSku(r ?? null))
      .catch(() => { /* se queda con lo que había */ });
  };

  const addMlaId = async () => {
    if (!selected || !newMlaId.trim()) return;
    if (!can('catalogo', 'editar')) { toast.addToast('No tenés permiso para editar','error'); return; }
    try { await api.post(`/api/v3/catalogo/${selected.skuInterno}/mla`, { mlaId: newMlaId.trim() }); setNewMlaId(''); loadMlaIds(selected.skuInterno); }
    catch (e) { toast.addToast(extractErrorMessage(e), 'error'); }
  };
  const removeMlaId = async (mlaId: string) => {
    if (!selected) return;
    if (!can('catalogo', 'editar')) { toast.addToast('No tenés permiso para editar','error'); return; }
    try { await api.delete(`/api/v3/catalogo/${selected.skuInterno}/mla/${mlaId}`); loadMlaIds(selected.skuInterno); }
    catch (e) { toast.addToast(extractErrorMessage(e), 'error'); }
  };

  const validateUM = (codigo: string, label: string) => {
    if (!codigo) return true;
    if (!unidades?.some(u => u.codigo === codigo)) { setUmError(`UM ${label} "${codigo}" no existe en el maestro`); return false; }
    setUmError(''); return true;
  };
  const ensureMarca = async (nombre: string) => { if (!nombre) return; await api.post('/api/v3/marcas', { nombre }); };
  const GHOST_FIELDS = new Set(['abcInversion','unidadMedidaAlt','factorConversion','largoCm','anchoCm','altoCm']);
  const stripGhost = (obj: Record<string, any>) => Object.fromEntries(Object.entries(obj).filter(([k,v]) => v !== '' && !GHOST_FIELDS.has(k)));

  const handleCreate = async () => {
    if (!validateUM(form.unidadMedidaBase, 'Base')) return;
    if (!validateUM(form.unidadMedidaAlt, 'Alt')) return;
    setSubmitting(true);
    try {
      await ensureMarca(form.marca);
      const payload = stripGhost({
        ...form,
        proveedorEstandarId: form.proveedorEstandarId ? Number(form.proveedorEstandarId) : undefined,
        proveedorId: form.proveedorId ? Number(form.proveedorId) : undefined,
      });
      await api.post('/api/v3/catalogo', payload);
      toast.addToast('Producto creado','success'); cerrarNuevo(); refetch();
    } catch (e) { toast.addToast(extractErrorMessage(e),'error'); }
    finally { setSubmitting(false); }
  };

  const EDITABLE_FIELDS = new Set(['descripcion','ean','abcCategoria','abcRotacion','abcRentabilidad','costoPromedio','precioVenta','margenBruto','margenPct','unidadMedidaBase','pesoKg','marca','titulo','activo','proveedorEstandarId','descuento','alicuotaIva']);
  const handleEdit = async () => {
    if (!selected) return;
    if (!validateUM(selected.unidadMedidaBase||'UN', 'Base')) return;
    setSubmitting(true);
    try {
      await ensureMarca(selected.marca||'');
      const payload: Record<string, any> = {};
      for (const [k, v] of Object.entries(selected)) {
        if (EDITABLE_FIELDS.has(k) && v !== '' && v !== undefined && v !== null) payload[k] = v;
      }
      if (selected.proveedorEstandarId) payload.proveedorEstandarId = Number(selected.proveedorEstandarId);
      await api.patch(`/api/v3/catalogo/${selected.skuInterno}`, payload);
      toast.addToast('Artículo actualizado','success'); setEditMode(false); refetch();
    } catch (e: any) {
      if (e?.status === 403) toast.addToast('No tenés permiso para editar este artículo','error');
      else toast.addToast(extractErrorMessage(e),'error');
    }
    finally { setSubmitting(false); }
  };

  const handleDeleteBom = async (hijo: string) => {
    if (!selected) return;
    if (!can('bom', 'eliminar')) { toast.addToast('No tenés permiso para eliminar componentes de la composición','error'); return; }
    try { await api.delete(`/api/v3/bom/${selected.skuInterno}/${hijo}`); toast.addToast('Eliminado','success'); loadBom(selected.skuInterno); }
    catch (e) { toast.addToast(extractErrorMessage(e),'error'); }
  };

  const input = (label:string, value:any, onChange:(v:string)=>void, type='text') => (
    <div><label className="block text-xs font-medium text-slate-600 mb-1">{label}</label><input type={type} aria-label={label} value={value} onChange={e=>onChange(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" /></div>
  );

  const setFilter = (k: string, v: string) => setFilters(f => v ? {...f,[k]:v} : Object.fromEntries(Object.entries(f).filter(([key]) => key !== k)));

  return (
    <div className="flex flex-col h-full gap-0">
      {/* Page header */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-800 inline">Base y publicaciones</h1>
        <HelpTip tips={[
          'Buscá un producto y mirá su ficha: stock, variantes, publicaciones y composición',
          'Creá un producto nuevo: es el primer paso antes de una orden de compra',
          'Mantené bien el stock en Mercado Libre: mirá qué no se está actualizando y destrabalo',
        ]} />
        <VerInstructivo seccion="base-y-publicaciones" />
        <p className="text-slate-500 text-sm mt-0.5">Maestro de artículos y publicaciones en MercadoLibre</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-nowrap gap-2 overflow-x-auto border-b border-slate-200 pb-0 mb-4">
        {SOLAPAS.map(t => {
          return (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`shrink-0 px-4 py-2 text-sm font-medium rounded-t-md border-b-2 transition-colors ${
                activeTab === t
                  ? 'border-blue-600 text-blue-700 bg-blue-50'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              {ROTULOS[t]}
            </button>
          );
        })}
      </div>

      {activeTab === 'productos' && (
        <>
          {/* Top bar actions */}
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={()=>setModalOpen(true)} disabled={!can('catalogo','crear')}>+ Nuevo producto</Button>
            </div>
          </div>

          {/* Search bar */}
          <div className="mb-3 relative">
            <svg className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              value={search}
              onChange={e=>{setSearch(e.target.value);setPage(1)}}
              placeholder="Buscar por SKU, descripción, marca…"
              className="w-full pl-11 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm bg-white shadow-sm focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-300"
            />
          </div>

          {/* Two-column layout */}
          <div className="flex gap-4 flex-1 min-h-0">
            {/* Filter panel */}
            <aside className="hidden lg:flex flex-col w-56 shrink-0 bg-white rounded-xl border border-slate-200 shadow-sm p-4 gap-5 self-start">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-700">Filtros</h3>
                {hasFilters && (
                  <button onClick={()=>setFilters({})} className="text-xs text-brand-600 hover:underline">Limpiar</button>
                )}
              </div>

              {/* A completar */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Estado</p>
                <button
                  onClick={() => setFilter('incompleto', filters.incompleto === 'true' ? '' : 'true')}
                  title="SKU sin descripción, marca, EAN, proveedor estándar, unidad de medida, peso o dimensiones"
                  className={`w-full px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${filters.incompleto === 'true' ? 'bg-brand-500 text-white border-brand-500' : 'border-slate-200 text-slate-500 hover:border-brand-300 hover:text-brand-600'}`}
                >A completar</button>
              </div>

              {/* Marca */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Marca</p>
                <input
                  value={filters.marca ?? ''}
                  onChange={e => setFilter('marca', e.target.value)}
                  placeholder="Filtrar por marca…"
                  className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-brand-400"
                />
              </div>

              {/* Proveedor */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Proveedor</p>
                <select
                  value={filters.proveedorNombre ?? ''}
                  onChange={e => setFilter('proveedorNombre', e.target.value)}
                  className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-brand-400"
                >
                  <option value="">Todos</option>
                  {(proveedoresList??[]).map(p=><option key={p.id} value={p.nombre}>{p.nombre}</option>)}
                </select>
              </div>

            </aside>

            {/* Product list */}
            <div className="flex-1 flex flex-col min-w-0 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              {/* List header */}
              <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                <span className="text-sm text-slate-500">
                  {loading ? 'Cargando…' : <><strong className="text-slate-700">{total.toLocaleString('es-AR')}</strong> artículos</>}
                </span>
              </div>

              {/* Rows */}
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-20 text-slate-400 text-sm">Cargando artículos…</div>
                ) : paged.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-slate-400 gap-2">
                    <svg className="w-10 h-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                    <span className="text-sm">Sin resultados</span>
                  </div>
                ) : (
                  paged.map(art => (
                    <ArticuloRow key={art.skuInterno} art={art} onClick={() => { setSelected(art); setDetailOpen(true); setEditMode(false); }} />
                  ))
                )}
              </div>

              {/* Pagination */}
              <div className="border-t border-slate-100 px-4 py-2">
                <Pagination page={page} pageSize={pageSize} total={total} onChange={setPage} />
              </div>
            </div>
          </div>

          {/* Create Modal */}
          <Modal open={modalOpen} cierre={cierreNuevo} title="Nuevo producto" size="xl">
            <div className="space-y-4">
              {tabBar(createTab, setCreateTab)}
              {createTab === 'basicos' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                  {[{l:'Código *',k:'codigo'},{l:'Descripción',k:'descripcion'},{l:'Título',k:'titulo'},{l:'EAN',k:'ean'},{l:'MLA ID',k:'mlaId'}].map(({l,k})=>input(l,(form as any)[k],v=>setForm({...form,[k]:v})))}
                  <div className="relative"><label className="block text-xs font-medium text-slate-600 mb-1">Marca</label>
                    <input value={marcaInput} onChange={e=>{setMarcaInput(e.target.value);setShowMarcaDropdown(true);setMarcaSuggestions((marcasList??[]).filter(m=>m.nombre.toLowerCase().includes(e.target.value.toLowerCase())));}} onFocus={()=>{setShowMarcaDropdown(true);setMarcaSuggestions(marcasList??[]);}} onBlur={()=>setTimeout(()=>setShowMarcaDropdown(false),200)} placeholder="Buscar o escribir nueva..." className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                    {showMarcaDropdown && marcaSuggestions.length > 0 && (<div className="absolute z-10 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">{marcaSuggestions.map(m=><div key={m.nombre} onClick={()=>{setMarcaInput(m.nombre);setForm({...form,marca:m.nombre});setShowMarcaDropdown(false);}} className="px-3 py-1.5 text-sm hover:bg-brand-50 cursor-pointer">{m.nombre}</div>)}</div>)}
                  </div>
                  <div><label className="block text-xs font-medium text-slate-600 mb-1" title="Proveedor del artículo: proveedor + código lo identifican">Proveedor</label>
                    <select aria-label="Proveedor" value={form.proveedorId} onChange={e=>setForm({...form,proveedorId:e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">
                      <option value="">—</option>{(proveedoresList??[]).map(p=><option key={p.id} value={p.id}>{p.nombre}</option>)}
                    </select>
                  </div>
                  <div><label className="block text-xs font-medium text-slate-600 mb-1">Proveedor estándar</label>
                    <select value={form.proveedorEstandarId} onChange={e=>setForm({...form,proveedorEstandarId:e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">
                      <option value="">—</option>{(proveedoresList??[]).map(p=><option key={p.id} value={p.id}>{p.nombre}</option>)}
                    </select>
                  </div>
                  <div><label className="block text-xs font-medium text-slate-600 mb-1" title="Descuento de compra del SKU. En la OC se sugiere como Desc. SKU (acumulativo con proveedor y marca)">Desc. SKU %</label>
                    <input type="number" min={0} max={100} step="0.01" value={form.descuento} onChange={e=>setForm({...form,descuento:e.target.value})} aria-label="Descuento de compra del SKU (%)" placeholder="0" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                  </div>
                  <div><label className="block text-xs font-medium text-slate-600 mb-1" title="Alícuota de IVA del SKU">IVA %</label>
                    <select value={form.alicuotaIva} onChange={e=>setForm({...form,alicuotaIva:e.target.value})} aria-label="Alícuota de IVA del SKU (%)" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">
                      {ALICUOTAS_IVA.map(a=><option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                </div>
              )}
              {createTab === 'dimensiones' && (<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">{[{l:'Peso (kg)',k:'pesoKg'},{l:'Largo (cm)',k:'largoCm'},{l:'Ancho (cm)',k:'anchoCm'},{l:'Alto (cm)',k:'altoCm'}].map(({l,k})=>input(l,(form as any)[k],v=>setForm({...form,[k]:v}),'number'))}</div>)}
              {createTab === 'unidades' && (<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <div><label className="block text-xs font-medium text-slate-600 mb-1">UM Base</label><select value={form.unidadMedidaBase} onChange={e=>setForm({...form,unidadMedidaBase:e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">{(unidades??[]).map(u=><option key={u.codigo} value={u.codigo}>{u.codigo} — {u.descripcion}</option>)}</select></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">UM Alt</label><select value={form.unidadMedidaAlt} onChange={e=>setForm({...form,unidadMedidaAlt:e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"><option value="">—</option>{(unidades??[]).map(u=><option key={u.codigo} value={u.codigo}>{u.codigo} — {u.descripcion}</option>)}</select></div>
                <div>{input('Factor Conv.', form.factorConversion, v=>setForm({...form,factorConversion:v}), 'number')}</div>
              </div>)}
              {umError && <p className="text-red-600 text-sm">{umError}</p>}
              <div className="flex justify-end gap-3 pt-4"><button onClick={cierreNuevo.cerrar} className="px-4 py-2 text-sm">Cancelar</button><Button onClick={handleCreate} loading={submitting}>{submitting ? 'Guardando...' : 'Crear'}</Button></div>
            </div>
          </Modal>

          {/* Detail/Edit Modal */}
          <Modal open={detailOpen} cierre={cierreDetalle} title={`Artículo ${selected?.codigo || selected?.skuInterno || ''}`} size="xl">
            {selected && !editMode && (
              <div className="space-y-4">
                {tabBar(detailTab, setDetailTab)}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  {detailTab === 'basicos' && ([['Descripcion',selected.descripcion],['Titulo',selected.titulo],['EAN',selected.ean],['MLA ID',selected.mlaId||'—'],['Marca',selected.marca],['Código',selected.codigo||selected.skuInterno],['Proveedor',selected.proveedorIdentidad?.nombre],['Proveedor estándar',selected.proveedorEstandar?.nombre],['Estado',selected.activo?'Activo':'Inactivo']].map(([l,v])=>(<div key={l}><span className="text-slate-500">{l}:</span><span className="font-medium ml-1">{v||'—'}</span></div>)))}
                  {detailTab === 'dimensiones' && ([['Peso (kg)',selected.pesoKg],['Largo (cm)',selected.largoCm],['Ancho (cm)',selected.anchoCm],['Alto (cm)',selected.altoCm]].map(([l,v])=>(<div key={l}><span className="text-slate-500">{l}:</span><span className="font-medium ml-1">{v||'—'}</span></div>)))}
                  {detailTab === 'unidades' && ([['UM Base',selected.unidadMedidaBase],['UM Alt',selected.unidadMedidaAlt],['Factor Conv.',selected.factorConversion]].map(([l,v])=>(<div key={l}><span className="text-slate-500">{l}:</span><span className="font-medium ml-1">{v||'—'}</span></div>)))}
                </div>
                {mlaIds.length > 0 && (<div className="pt-2 border-t"><h3 className="text-sm font-semibold text-slate-700 mb-1">Otras publicaciones (MLA): {mlaIds.length}</h3><div className="flex flex-wrap gap-2">{mlaIds.map(m=>(<span key={m.id} className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${m.activo === false ? 'bg-slate-100 text-slate-400 line-through' : 'bg-blue-50 text-blue-700'}`} title={m.activo === false ? 'Vínculo desactivado: ya no está en la planilla 02_CODIGOS' : undefined}>{m.mlaId}</span>))}</div></div>)}
                {publicacionesSku && (
                  <div className="pt-2 border-t space-y-2">
                    {publicacionesSku.stock !== undefined && (
                      <section aria-label="Stock del SKU" className="space-y-1">
                        <h3 className="text-sm font-semibold text-slate-700">Stock</h3>
                        <StockSku stock={publicacionesSku.stock} variantes={variantesSku} />
                      </section>
                    )}
                    <Link to={`/variantes?sku=${encodeURIComponent(selected.skuInterno)}`} className="block text-sm text-brand-600 hover:underline">Variantes de este SKU</Link>
                    <VariantesSku sku={selected.skuInterno} puedeEditar={can('catalogo', 'editar')} onCargadas={setVariantesSku} publicaciones={{ directas: publicacionesSku.directas, indirectas: publicacionesSku.indirectas.flatMap(i => i.publicaciones) }} />
                    <h3 className="text-sm font-semibold text-slate-700">¿En qué publicaciones se usa este SKU?</h3>
                    {publicacionesSku.directas.length === 0 && publicacionesSku.indirectas.length === 0 && (
                      <p className="text-xs text-slate-500">No está en ninguna publicación, ni directa ni como componente de otro SKU.</p>
                    )}
                    {publicacionesSku.directas.length > 0 && (<>
                      <p className="text-xs font-medium text-slate-600">Directas ({publicacionesSku.directas.length})</p>
                      <TablaPublicacionesSku items={publicacionesSku.directas} />
                    </>)}
                    {publicacionesSku.indirectas.length > 0 && (<>
                      <p className="text-xs font-medium text-slate-600">Como componente de otros SKU ({publicacionesSku.indirectas.length})</p>
                      {publicacionesSku.indirectas.map(i => (
                        <div key={i.skuInterno} className="rounded-lg border border-slate-200 p-2">
                          <div className="text-xs text-slate-700 mb-1">
                            <span className="font-semibold"><CodigoArticulo sku={i.skuInterno} /></span>
                            {(i.titulo || i.descripcion) && <span className="text-slate-500"> — {i.titulo || i.descripcion}</span>}
                            {!i.activo && <span className="text-slate-400 ml-1">(inactivo)</span>}
                            <span className="text-slate-500"> · lleva {i.cantidad} u. · camino: {i.camino.map(s => etiquetaArticulo(s, identidad(s))).join(' → ')}</span>
                          </div>
                          {i.publicaciones.length > 0
                            ? <TablaPublicacionesSku items={i.publicaciones} />
                            : <p className="text-xs text-slate-400">Este SKU no tiene publicaciones.</p>}
                        </div>
                      ))}
                    </>)}
                  </div>
                )}
                {bomItems.length > 0 && (<><h3 className="text-sm font-semibold text-slate-700 pt-2 border-t">Composición ({bomItems.length})</h3><div className="overflow-x-auto"><table className="w-full text-sm"><thead className="bg-slate-50"><tr><th className="text-left px-2 py-1">Código hijo</th><th className="text-left px-2 py-1">Descripcion</th><th className="text-right px-2 py-1">Cant.</th><th className="text-center px-2 py-1">UM</th></tr></thead><tbody>{bomItems.map(b=>(<tr key={b.skuHijo} className="border-t border-slate-50"><td className="px-2 py-1"><CodigoArticulo sku={b.skuHijo} /></td><td className="px-2 py-1">{b.hijo?.descripcion||'—'}</td><td className="text-right px-2 py-1">{b.cantidad}</td><td className="text-center px-2 py-1">{b.hijo?.unidadMedidaBase||'—'}</td></tr>))}</tbody></table></div></>)}
                {dondeSeUsa.length > 0 && (<><h3 className="text-sm font-semibold text-slate-700 pt-2 border-t">Dónde se usa ({dondeSeUsa.length})</h3><div className="overflow-x-auto"><table className="w-full text-sm"><thead className="bg-slate-50"><tr><th className="text-left px-2 py-1">Código padre</th><th className="text-left px-2 py-1">Descripcion</th><th className="text-right px-2 py-1">Cant.</th><th className="text-center px-2 py-1">UM</th></tr></thead><tbody>{dondeSeUsa.map(d=>(<tr key={d.skuPadre} className="border-t border-slate-50"><td className="px-2 py-1"><CodigoArticulo sku={d.skuPadre} />{d.padre?.activo===false && <span className="text-slate-400 text-xs ml-1">(inactivo)</span>}</td><td className="px-2 py-1">{d.padre?.descripcion||'—'}</td><td className="text-right px-2 py-1">{d.cantidad}</td><td className="text-center px-2 py-1">{d.padre?.unidadMedidaBase||'—'}</td></tr>))}</tbody></table></div></>)}
                <section aria-label="Cambios en Mercado Libre" className="pt-2 border-t space-y-2"><h3 className="text-sm font-semibold text-slate-700">Cambios en Mercado Libre</h3><HistorialMl sku={selected.skuInterno} /></section>
                <PanelDesplegable titulo="Mercado Libre"><VistaPreviaSyncSku sku={selected.skuInterno} onCambio={recargarFicha} /></PanelDesplegable>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button onClick={()=>setDetailOpen(false)} className="px-4 py-2 text-sm">Cerrar</button>
                  <Button onClick={()=>setEditMode(true)} disabled={!can('catalogo','editar')}>Editar</Button>
                </div>
              </div>
            )}
            {selected && editMode && (
              <div className="space-y-4">
                {tabBar(editTab, setEditTab)}
                {editTab === 'basicos' && (<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {[{l:'Descripción',k:'descripcion'},{l:'Título',k:'titulo'},{l:'EAN',k:'ean'},{l:'MLA ID',k:'mlaId'}].map(({l,k})=>input(l,(selected as any)[k]??'',v=>setSelected({...selected,[k]:v})))}
                  <div className="relative"><label className="block text-xs font-medium text-slate-600 mb-1">Marca</label>
                    <input value={editMarcaInput||selected.marca||''} onChange={e=>{setEditMarcaInput(e.target.value);setSelected({...selected,marca:e.target.value});setShowEditMarcaDropdown(true);setMarcaSuggestions((marcasList??[]).filter(m=>m.nombre.toLowerCase().includes(e.target.value.toLowerCase())));}} onFocus={()=>{setEditMarcaInput(selected.marca||'');setShowEditMarcaDropdown(true);setMarcaSuggestions(marcasList??[]);}} onBlur={()=>setTimeout(()=>setShowEditMarcaDropdown(false),200)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                    {showEditMarcaDropdown && marcaSuggestions.length > 0 && (<div className="absolute z-10 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">{marcaSuggestions.map(m=><div key={m.nombre} onClick={()=>{setEditMarcaInput(m.nombre);setSelected({...selected,marca:m.nombre});setShowEditMarcaDropdown(false);}} className="px-3 py-1.5 text-sm hover:bg-brand-50 cursor-pointer">{m.nombre}</div>)}</div>)}
                  </div>
                  <div><label className="block text-xs font-medium text-slate-600 mb-1">Proveedor estándar</label><select value={selected.proveedorEstandarId??''} onChange={e=>setSelected({...selected,proveedorEstandarId:e.target.value?Number(e.target.value):undefined})} className="w-full px-3 py-2 border rounded-lg text-sm"><option value="">—</option>{(proveedoresList??[]).map(p=><option key={p.id} value={p.id}>{p.nombre}</option>)}</select></div>
                  <div><label className="block text-xs font-medium text-slate-600 mb-1" title="Descuento de compra del SKU. En la OC se sugiere como Desc. SKU (acumulativo con proveedor y marca)">Desc. SKU %</label><input type="number" min={0} max={100} step="0.01" value={selected.descuento??''} onChange={e=>setSelected({...selected,descuento:e.target.value})} aria-label="Descuento de compra del SKU (%)" placeholder="0" className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
                  <div><label className="block text-xs font-medium text-slate-600 mb-1" title="Alícuota de IVA del SKU">IVA %</label><select value={alicuotaIvaTexto(selected.alicuotaIva)} onChange={e=>setSelected({...selected,alicuotaIva:e.target.value})} aria-label="Alícuota de IVA del SKU (%)" className="w-full px-3 py-2 border rounded-lg text-sm">{ALICUOTAS_IVA.map(a=><option key={a} value={a}>{a}</option>)}</select></div>
                  <div><label className="block text-xs font-medium text-slate-600 mb-1">Activo</label><select value={selected.activo?'true':'false'} onChange={e=>setSelected({...selected,activo:e.target.value==='true'})} className="w-full px-3 py-2 border rounded-lg text-sm"><option value="true">Activo</option><option value="false">Inactivo</option></select></div>
                </div>)}
                <div className="pt-2 border-t"><h3 className="text-sm font-semibold text-slate-700 mb-2">Otras publicaciones (MLA): {mlaIds.length}</h3>
                  <div className="flex flex-wrap gap-2 mb-2">{mlaIds.map(m=>(<span key={m.id} className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">{m.mlaId}<button onClick={()=>removeMlaId(m.mlaId)} disabled={!can('catalogo','editar')} className="ml-1.5 hover:text-red-500 font-bold disabled:opacity-50">&times;</button></span>))}</div>
                  <div className="flex gap-2"><input value={newMlaId} onChange={e=>setNewMlaId(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addMlaId()} placeholder="MLA-XXXXXXXXXXX" className="px-3 py-1.5 border rounded-lg text-sm w-56" /><Button size="sm" onClick={addMlaId} disabled={!newMlaId.trim()||!can('catalogo','editar')}>Agregar</Button></div>
                </div>
                {editTab === 'dimensiones' && (<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">{[{l:'Peso (kg)',k:'pesoKg'},{l:'Largo (cm)',k:'largoCm'},{l:'Ancho (cm)',k:'anchoCm'},{l:'Alto (cm)',k:'altoCm'}].map(({l,k})=>input(l,(selected as any)[k]??'',v=>setSelected({...selected,[k]:v}),'number'))}</div>)}
                {editTab === 'unidades' && (<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div><label className="block text-xs font-medium text-slate-600 mb-1">UM Base</label><select value={selected.unidadMedidaBase??'UN'} onChange={e=>setSelected({...selected,unidadMedidaBase:e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm">{(unidades??[]).map(u=><option key={u.codigo} value={u.codigo}>{u.codigo} — {u.descripcion}</option>)}</select></div>
                  <div><label className="block text-xs font-medium text-slate-600 mb-1">UM Alt</label><select value={selected.unidadMedidaAlt??''} onChange={e=>setSelected({...selected,unidadMedidaAlt:e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm"><option value="">—</option>{(unidades??[]).map(u=><option key={u.codigo} value={u.codigo}>{u.codigo} — {u.descripcion}</option>)}</select></div>
                  <div>{input('Factor Conv.', selected.factorConversion??'', v=>setSelected({...selected,factorConversion:v}), 'number')}</div>
                </div>)}
                {umError && <p className="text-red-600 text-sm">{umError}</p>}
                {bomItems.length > 0 && (<><h3 className="text-sm font-semibold text-slate-700 pt-2 border-t">Composición ({bomItems.length})</h3><div className="overflow-x-auto"><table className="w-full text-sm"><thead className="bg-slate-50"><tr><th className="text-left px-2 py-1">Código hijo</th><th className="text-left px-2 py-1">Desc</th><th className="text-right px-2 py-1">Cant.</th><th></th></tr></thead><tbody>{bomItems.map(b=>(<tr key={b.skuHijo} className="border-t"><td className="px-2 py-1"><CodigoArticulo sku={b.skuHijo} /></td><td className="px-2 py-1 text-xs">{b.hijo?.descripcion||'—'}</td><td className="text-right px-2 py-1">{b.cantidad}</td><td className="text-center px-2 py-1"><button onClick={()=>handleDeleteBom(b.skuHijo)} disabled={!can('bom','eliminar')} className="text-red-600 text-xs disabled:opacity-50">X</button></td></tr>))}</tbody></table></div></>)}
                {dondeSeUsa.length > 0 && (<><h3 className="text-sm font-semibold text-slate-700 pt-2 border-t">Dónde se usa ({dondeSeUsa.length})</h3><div className="overflow-x-auto"><table className="w-full text-sm"><thead className="bg-slate-50"><tr><th className="text-left px-2 py-1">Código padre</th><th className="text-left px-2 py-1">Descripcion</th><th className="text-right px-2 py-1">Cant.</th><th className="text-center px-2 py-1">UM</th></tr></thead><tbody>{dondeSeUsa.map(d=>(<tr key={d.skuPadre} className="border-t border-slate-50"><td className="px-2 py-1"><CodigoArticulo sku={d.skuPadre} />{d.padre?.activo===false && <span className="text-slate-400 text-xs ml-1">(inactivo)</span>}</td><td className="px-2 py-1">{d.padre?.descripcion||'—'}</td><td className="text-right px-2 py-1">{d.cantidad}</td><td className="text-center px-2 py-1">{d.padre?.unidadMedidaBase||'—'}</td></tr>))}</tbody></table></div></>)}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button onClick={()=>setEditMode(false)} className="px-4 py-2 text-sm">Cancelar</button>
                  <Button onClick={handleEdit} loading={submitting}>{submitting ? 'Guardando...' : 'Guardar'}</Button>
                </div>
              </div>
            )}
          </Modal>

          {/* Import progress modal */}
        </>
      )}


      {activeTab === 'mercado-libre' && (
        <div className="space-y-4">
          <SincronizarConMl debajoDelAviso={<PublicacionesMl />} />
          <PanelDesplegable titulo="Historial de cambios en Mercado Libre" abrir={historial ? 1 : 0}>
            <HistorialMl />
          </PanelDesplegable>
        </div>
      )}

    </div>
  );
}

